import React, { useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import WithSection from '../../components/WithSection/WithSection';
import { css } from 'styled-components';
import {
    Head2,
    Caption,
    Body1,
    Body2,
    Headline,
} from '../../components/Typography/Typography';
import Box from '../../components/Box/Box';
import Image from '../../components/Image/Image';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import CtaSlots from '../../slots/CtaSlots/CtaSlots';
import { isLightColor } from '../../utils/isLightColor';
import { rem } from 'polished';
import Inline from '../../components/Inline/Inline';
import { VideoPlayIcon } from '../../icons/VideoPlayIcon';
import BookingFormModal from '../../components/BookingFormModal/BookingFormModal';
import { VideoThumbnail } from '../../components/VideoThumbnail/VideoThumbnail';
import RichText from '../../components/RichText/RichText';
import { WatchAVideoButtonLink } from '../../components/WatchAVideoButtonLink/WatchAVideoButtonLink';
import Button from '../../components/Button/Button';
import ChevronRight from '../../images/icons/chevron-right.svg';
import { track } from '../../utils/analytics';

const HeroSection = ({
    data: { primary },
    styling: { backgroundVariant: sectionBackground },
}) => {
    const {
        heading,
        section_subheading,
        description,
        cta,
        image,
        rating_with_badges,
        video_embed_link,
        watch_a_video_button,
        watch_a_video_button_tracking_event_name,
    } = primary;
    const isDarkBackground = !isLightColor(sectionBackground);
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Box
                display="grid"
                gridColumn={{
                    _: 'span 4',
                    sm: '2/-2',
                    md: '1/span 3',
                    lg: '1/span 5',
                    xl: '2/span 4',
                }}
                justifyItems={{ _: 'center', md: 'start' }}
                textAlign={{ _: 'center', md: 'start' }}
                alignContent="center"
            >
                <Head2
                    py={rem(8)}
                    bold
                    {...(isDarkBackground && { color: 'neutral.white' })}
                >
                    <Box
                        css={css`
                            * {
                                margin: 0;
                            }
                        `}
                    >
                        <RichText render={heading.richText} />
                    </Box>
                </Head2>
                {section_subheading && (
                    <Box mb={rem(16)} maxWidth={{ lg: '85%' }}>
                        <Headline color="neutral.1200" fontWeight="normal">
                            {section_subheading}
                        </Headline>
                    </Box>
                )}
                {description && (
                    <Box
                        as={Body1}
                        mt={rem(8)}
                        py={rem(8)}
                        color={
                            isDarkBackground ? 'primary.200' : 'primary.1600'
                        }
                        maxWidth={{ lg: '90%' }}
                    >
                        <RichText render={description.richText} />
                    </Box>
                )}
                {cta?.document && (
                    <Box
                        display="flex"
                        justifyContent={{ xs: 'center', md: 'start' }}
                        flexDirection={{ _: 'column', xs: 'row' }}
                        mt={rem(16)}
                        py={rem(8)}
                        width="100%"
                        gap={rem(16)}
                    >
                        <CtaSlots CTA={cta} />
                        {watch_a_video_button && video_embed_link?.url && (
                            <WatchAVideoButtonLink
                                text={watch_a_video_button}
                                videoEmbedUrl={video_embed_link?.url}
                                trackingEvent={
                                    watch_a_video_button_tracking_event_name
                                }
                            />
                        )}
                    </Box>
                )}

                {rating_with_badges?.document && (
                    <Box
                        bg="neutral.100"
                        py="16px"
                        px="17px"
                        display="flex"
                        alignItems="center"
                        borderRadius="4px"
                        mt={rem(16)}
                        width={{ sm: 'fit-content' }}
                        justifyContent="space-around"
                        gap={rem(20)}
                    >
                        <Image
                            img={
                                rating_with_badges?.document?.data?.rating_stars
                            }
                        />
                        <Box
                            display="flex"
                            justifyContent="space-around"
                            css={css`
                                gap: 8px;
                            `}
                        >
                            {rating_with_badges?.document?.data?.badges.map(
                                ({ badge, link }) => (
                                    <PrismicLink
                                        link={link}
                                        css={css`
                                            pointer-events: ${!link.url
                                                ? 'none'
                                                : 'initial'};
                                        `}
                                    >
                                        <Image img={badge} />
                                    </PrismicLink>
                                )
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            <Box
                gridColumn={{
                    _: '1/-1',
                    md: 'span 5',
                    lg: 'span 7',
                }}
                marginTop={{ _: rem(32), md: 0 }}
                display="grid"
                placeItems="center"
            >
                <VideoThumbnail
                    enableVideo={!watch_a_video_button}
                    video_embed_link={video_embed_link}
                    image={image}
                />
            </Box>
        </>
    );
};

// TODO: convert to fragments
export const _ = graphql`
    fragment HERO_SECTION on PrismicHeroDefault {
        primary {
            heading {
                richText
            }
            section_subheading
            description {
                richText
            }
            cta {
                document {
                    ... on PrismicCta {
                        data {
                            body {
                                ... on PrismicCtaDataBodyButtons {
                                    items {
                                        __typename
                                        text
                                        size
                                        variant
                                        tracking_event
                                        link {
                                            ...PRISMIC_LINK_FRAGMENT
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            image {
                gatsbyImageData(layout: CONSTRAINED, height: 800)
                alt
                url
            }
            video_embed_link {
                url
            }
            watch_a_video_button
            watch_a_video_button_tracking_event_name
            rating_with_badges {
                document {
                    ... on PrismicRatingWithBadges {
                        id
                        data {
                            rating_stars {
                                alt
                                url
                                dimensions {
                                    height
                                }
                            }
                            badges {
                                badge {
                                    alt
                                    url
                                }
                                link {
                                    ...PRISMIC_LINK_FRAGMENT
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export default WithSection(HeroSection);
